import React, { useState, useEffect, useMemo, useRef } from "react";

import { toast } from 'react-toastify';
import {
  FaSearch,
  FaTrash,
  FaFileAlt,
  FaFilePdf,
  FaFileImage,
  FaFile,
  FaUpload,
  FaClipboardList,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileVideo
} from 'react-icons/fa';

import { getProjectDocuments, uploadProjectDocumentV1, deleteProjectDocumentV1 } from '../../api/documents';
import { getTableHeaderStyle, DesignSystem } from '../../styles/designSystem';
import { LoadingSpinner } from '../LoadingSpinner';
import { DocumentUploadModal } from '../DocumentUploadModal';
import ConfirmationDialog from '../ConfirmationDialog';

// Updated interface based on actual API response
interface DocumentData {
  id: string;
  name: string;
  file_name?: string;
  file_size: number;
  content_type: string;
  description?: string;
  document_kind: string;
  version: number;
  url: string;
  created_at: string;
  updated_at?: string;
  owner: {
    email: string;
    username: string;
    first_name: string;
    last_name: string;
    id: string;
  };
}

interface TabDocumentsProps {
  projectId: string | undefined;
  token: string | undefined;
  onNavigateToTab?: (tab: string) => void;
}

export const TabDocuments = ({ 
  projectId, 
  token,
  onNavigateToTab
}: TabDocumentsProps) => {
  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState<'single' | 'bulk'>('single');
  const [downloadingDocuments, setDownloadingDocuments] = useState<Set<string>>(new Set());
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Scroll detection for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load documents on mount. Guard against React StrictMode's dev-only
  // double-invoke firing this load twice, while still reloading on a genuine
  // projectId change (this component does not remount when switching
  // projects while already on the Documents tab). Keyed only on projectId,
  // not token: getProjectDocuments takes an unused `_token` no-op param
  // (auth is handled internally by authenticatedFetch), so keying on token
  // too would spuriously re-fetch every time it rotates.
  const lastLoadedProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!projectId || !token || lastLoadedProjectIdRef.current === projectId) return;
    lastLoadedProjectIdRef.current = projectId;
    loadDocuments();
  }, [projectId, token]);

  // Load documents from API
  const loadDocuments = async () => {
    if (!token || !projectId) return;
    
    try {
      setIsLoading(true);
      console.log('Loading documents for project:', projectId);
      
      const response = await getProjectDocuments(token, projectId);
      console.log('Documents API response:', response);
      
      if (response.results) {
        // Cast to DocumentData[] since we know the actual API structure
        const documents = response.results as any as DocumentData[];
        // Sort by created_at descending (newest first)
        const sortedDocs = documents.sort((a, b) => 
          new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
        );
        setDocuments(sortedDocs);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter documents based on search query
  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents;
    
    const query = searchQuery.toLowerCase();
    return documents.filter(doc => 
      doc.name.toLowerCase().includes(query) ||
      doc.document_kind.toLowerCase().includes(query) ||
      doc.content_type.toLowerCase().includes(query) ||
      doc.owner.first_name.toLowerCase().includes(query) ||
      doc.owner.last_name.toLowerCase().includes(query)
    );
  }, [documents, searchQuery]);

  // Get source type display name (plural form to match tab names)
  const getSourceDisplay = (document_kind: string) => {
    switch (document_kind) {
      case 'Task': return 'Project Plan';
      case 'ActionItem': 
      case 'Action Item': // Backend might send with space
        return 'Action Items'; // Plural to match tab name
      case 'Meeting': return 'Meeting Notes'; // Plural to match tab name
      case 'DailyUpdate': 
      case 'Daily Update': // Backend might send with space
        return 'Daily Updates'; // Plural to match tab name
      default: return 'Project Document';
    }
  };

  // Map document_kind to tab value
  const getTabForDocumentKind = (document_kind: string): string => {
    switch (document_kind) {
      case 'Task': return 'project-plan-2';
      case 'ActionItem': 
      case 'Action Item': // Backend might send with space
        return 'action-items';
      case 'Meeting': return 'meeting-notes';
      case 'DailyUpdate': 
      case 'Daily Update': // Backend might send with space
        return 'daily-updates';
      default: return 'documents';
    }
  };

  // Get file type icon
  const getFileIcon = (_fileName: string, contentType: string) => {
    if (contentType.includes('pdf')) {
      return <FaFilePdf className="w-4 h-4 text-red-500" />;
    } else if (contentType.includes('word')) {
      return <FaFileWord className="w-4 h-4 text-[#888]" />;
    } else if (contentType.includes('excel') || contentType.includes('spreadsheet')) {
      return <FaFileExcel className="w-4 h-4 text-green-500" />;
    } else if (contentType.includes('powerpoint') || contentType.includes('presentation')) {
      return <FaFilePowerpoint className="w-4 h-4 text-orange-500" />;
    } else if (contentType.includes('image')) {
      return <FaFileImage className="w-4 h-4 text-purple-500" />;
    } else if (contentType.includes('video')) {
      return <FaFileVideo className="w-4 h-4 text-red-500" />;
    } else if (contentType.includes('text')) {
      return <FaFileAlt className="w-4 h-4 text-green-500" />;
    } else {
      return <FaFile className="w-4 h-4 text-[#888]" />;
    }
  };

  // Get file type display name
  const getFileType = (fileName: string, contentType: string) => {
    if (contentType.includes('pdf')) return 'PDF';
    if (contentType.includes('word')) return 'Word';
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'Excel';
    if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'PowerPoint';
    if (contentType.includes('image')) return 'Image';
    if (contentType.includes('video')) return 'Video';
    if (contentType.includes('text')) return 'Text';
    
    const ext = fileName.split('.').pop()?.toUpperCase();
    return ext || 'File';
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Handle document download
  const handleDownload = async (document: DocumentData) => {
    if (!token || !document.url) {
      toast.error('Download URL not available');
      return;
    }
    
    // Add document to downloading set
    setDownloadingDocuments(prev => new Set([...prev, document.id]));
    
    try {
      console.log('Opening document:', document.name, 'URL:', document.url);
      
      // Check if file can be opened in browser based on content type
      const openableTypes = ['pdf', 'image', 'text'];
      const canOpen = openableTypes.some(type => 
        document.content_type.toLowerCase().includes(type)
      );

      // Use fetch with authorization header (reliable method)
      const response = await fetch(document.url, {
        method: 'GET',
        headers: {
          'authorization': 'Bearer ' + token
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      if (canOpen) {
        // Open viewable files in new tab
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        toast.success('Document opened successfully');
      } else {
        // Download non-viewable files
        const a = window.document.createElement('a');
        a.href = url;
        a.download = document.name;
        window.document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
        toast.success('Document downloaded successfully');
      }
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download document');
    } finally {
      // Remove document from downloading set
      setDownloadingDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(document.id);
        return newSet;
      });
    }
  };

  // Handle file upload from modal
  const handleUploadFromModal = async (file: File) => {
    if (!token || !projectId) return;
    
    try {
      await uploadProjectDocumentV1(
        token, 
        projectId, 
        file, 
        '' // description - можно добавить поле в модальное окно позже
      );
      toast.success('Document uploaded successfully');
    } catch (e: any) {
      console.error('Upload document failed (will recheck list):', e);
      // Suppress confusing auth toast; just inform we're rechecking
      toast.info('Verifying upload status...');
    } finally {
      // Small delay to allow backend to finalize
      setTimeout(() => {
        loadDocuments();
      }, 500);
    }
  };

  // Handle document deletion - show confirmation dialog
  const handleDelete = (document: DocumentData) => {
    setDocumentToDelete(document);
    setShowDeleteConfirmation(true);
  };

  // Actual delete function after confirmation
  const confirmDeleteDocument = async () => {
    if (!token || !projectId || !documentToDelete) return;

    setIsDeleting(true);
    try {
      console.log('🗑️ Deleting document:', {
        documentId: documentToDelete.id,
        documentName: documentToDelete.name,
        fileName: documentToDelete.file_name,
        documentKind: documentToDelete.document_kind,
        projectId: projectId
      });

      // Use document_id for the API endpoint: DELETE /api/v1/projects/{project_id}/document/{document_id}
      await deleteProjectDocumentV1(token, projectId, documentToDelete.id);
      console.log('✅ Document deleted successfully');
      toast.success('Document deleted successfully');
      
      // Dispatch event for related tabs to refresh their data
      const documentKind = documentToDelete.document_kind;
      console.log('📢 [TabDocuments] Raw document_kind from API:', documentKind);
      console.log('📢 [TabDocuments] Document ID:', documentToDelete.id);
      console.log('📢 [TabDocuments] Project ID:', projectId);
      
      // Use actual API values from TypeScript types: 'Meeting' | 'ActionItem' | 'DailyUpdate' | 'Task'
      // Also handle variants with spaces that backend might send
      if (documentKind === 'Meeting') {
        console.log('🚀 [TabDocuments] Dispatching meetingNoteDocumentDeleted event');
        window.dispatchEvent(new CustomEvent('meetingNoteDocumentDeleted', { 
          detail: { projectId, documentId: documentToDelete.id } 
        }));
        console.log('✅ [TabDocuments] Event dispatched successfully');
      } else if (documentKind === 'DailyUpdate' || documentKind === 'Daily Update') {
        console.log('🚀 [TabDocuments] Dispatching dailyUpdateDocumentDeleted event');
        window.dispatchEvent(new CustomEvent('dailyUpdateDocumentDeleted', { 
          detail: { projectId, documentId: documentToDelete.id } 
        }));
        console.log('✅ [TabDocuments] Event dispatched successfully');
      } else if (documentKind === 'ActionItem' || documentKind === 'Action Item') {
        console.log('🚀 [TabDocuments] Dispatching actionItemDocumentDeleted event');
        window.dispatchEvent(new CustomEvent('actionItemDocumentDeleted', { 
          detail: { projectId, documentId: documentToDelete.id } 
        }));
        console.log('✅ [TabDocuments] Event dispatched successfully');
      } else if (documentKind === 'Task') {
        console.log('🚀 [TabDocuments] Dispatching taskDocumentDeleted event');
        window.dispatchEvent(new CustomEvent('taskDocumentDeleted', { 
          detail: { projectId, documentId: documentToDelete.id } 
        }));
        console.log('✅ [TabDocuments] Event dispatched successfully');
      } else {
        console.log('⚠️ [TabDocuments] Unknown document kind:', documentKind, '- no event dispatched');
      }
      
      // Close dialog and reload documents
      setShowDeleteConfirmation(false);
      setDocumentToDelete(null);
      await loadDocuments();
    } catch (error) {
      console.error('❌ Delete failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete document';
      console.error('❌ Error details:', errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };



  return (
    <div className="min-h-screen">
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div className="sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className={`bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-shadow duration-200 ${isScrolled ? 'shadow-[0_4px_12px_rgba(0,0,0,0.06)]' : ''}`} style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Search and Actions Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #eaeaea' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Search and Quick Stats */}
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#aaa] w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search documents..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 w-80 text-[13px] text-[#444] bg-[#fafafa] border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0 focus:bg-white transition-all duration-150 placeholder-[#aaa]"
                    />
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-[#888]">
                      {filteredDocuments.length} of {documents.length} documents
                    </span>
                  </div>
                </div>

                {/* Right side - Actions */}
                <div className="flex items-center space-x-3">
                  {/* Upload Button */}
                  <button
                    onClick={() => {
                      setUploadMode('bulk');
                      setIsUploadModalOpen(true);
                    }}
                    className="flex items-center space-x-2 bg-[#171717] text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-[#333] transition-colors duration-150"
                  >
                    <FaUpload className="w-3.5 h-3.5" />
                    <span>Upload</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Table Header */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                <div className="flex-1" role="columnheader">Document</div>
                <div className="mx-6 w-24" role="columnheader">Type</div>
                <div className="mx-6 w-32" role="columnheader">Source</div>
                <div className="mx-6 w-40" role="columnheader">Owner</div>
                <div className="mx-6 w-20" role="columnheader">Size</div>
                <div className="ml-6 w-20" role="columnheader">Actions</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content - Scrollable Content */}
      <div className="max-w-[1280px] mx-auto px-0 -mt-4">
        <div className="bg-white">
          {isLoading ? (
            <LoadingSpinner text="Loading documents..." />
          ) : filteredDocuments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-[#888]">
              <FaClipboardList className="w-12 h-12 mb-4 text-[#ccc]" />
              {searchQuery ? (
                <>
                  <h3 className="text-lg font-medium mb-2">No documents found</h3>
                  <p>Try adjusting your search query</p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium mb-2">No documents yet</h3>
                  <p className="mb-4">Upload your first document to get started</p>
                  <button
                    onClick={() => {
                      setUploadMode('bulk');
                      setIsUploadModalOpen(true);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-[#171717] text-white rounded-lg hover:bg-[#333] transition-colors duration-150 text-[13px] font-medium"
                  >
                    <FaUpload className="w-4 h-4" />
                    <span>Upload Documents</span>
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="bg-white">
              {filteredDocuments.map((document) => (
                <DocumentRow 
                  key={document.id} 
                  document={document} 
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                  getFileIcon={getFileIcon}
                  getFileType={getFileType}
                  getSourceDisplay={getSourceDisplay}
                  getTabForDocumentKind={getTabForDocumentKind}
                  onNavigateToTab={onNavigateToTab}
                  formatFileSize={formatFileSize}
                  formatDate={formatDate}
                  isDownloading={downloadingDocuments.has(document.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUploadFromModal}
        title="Upload Documents"
        maxFileSize={100}
        description={uploadMode === 'bulk' ? "Select multiple documents or drag and drop them here" : "Select a document or drag and drop it here"}
        allowMultiple={uploadMode === 'bulk'}
        maxFiles={20}
      />

      {/* Delete Document Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setDocumentToDelete(null);
        }}
        onConfirm={confirmDeleteDocument}
        title="Delete Document"
        message={`Are you sure you want to delete "${documentToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete Document"
        cancelText="Cancel"
        confirmButtonClass="bg-[#dc2626] hover:bg-[#b91c1c]"
        isLoading={isDeleting}
      />
    </div>
  );
};

const DocumentRow = ({ 
  document, 
  onDownload, 
  onDelete,
  getFileIcon,
  getFileType,
  getSourceDisplay,
  getTabForDocumentKind,
  onNavigateToTab,
  formatFileSize,
  formatDate,
  isDownloading
}: {
  document: DocumentData;
  onDownload: (doc: DocumentData) => void;
  onDelete: (doc: DocumentData) => void;
  getFileIcon: (fileName: string, contentType: string) => React.ReactElement;
  getFileType: (fileName: string, contentType: string) => string;
  getSourceDisplay: (document_kind: string) => string;
  getTabForDocumentKind: (document_kind: string) => string;
  onNavigateToTab?: (tab: string) => void;
  formatFileSize: (bytes: number) => string;
  formatDate: (dateString: string) => string;
  isDownloading: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleSourceClick = () => {
    if (onNavigateToTab && document.document_kind !== 'Project') {
      const targetTab = getTabForDocumentKind(document.document_kind);
      onNavigateToTab(targetTab);
    }
  };

  return (
    <div 
      className={`group transition-all duration-200 ${
        isDownloading 
          ? 'cursor-wait bg-[#f5f5f5]/30' 
          : 'cursor-pointer hover:bg-[#fafafa]/50'
      } ${isHovered && !isDownloading ? 'bg-[#fafafa]/50' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => !isDownloading && onDownload(document)}
    >
      <div className="flex items-center px-6 py-4 relative" style={{ borderBottom: '0.5px solid #eaeaea' }}>
        {/* Document Info */}
        <div className="flex-1 flex items-center space-x-3 min-w-0">
          {getFileIcon(document.name, document.content_type)}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <span className={`text-sm font-medium truncate transition-colors ${
                isDownloading ? 'text-[#888]' : 'text-[#171717] hover:text-[#333]'
              }`}>
                {document.name}
              </span>
              <span className="text-xs text-[#888] flex-shrink-0">
                v{document.version}
              </span>
              {isDownloading ? (
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 border-2 border-[#888] border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs text-[#171717]">Opening...</span>
                </div>
              ) : (
                <svg className="w-3 h-3 text-[#aaa] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              )}
            </div>
            <div className="flex items-center space-x-2 text-xs text-[#888]">
              <span className="flex-shrink-0">{formatDate(document.created_at)}</span>
              {document.description && (
                <span className="truncate">• {document.description}</span>
              )}
            </div>
          </div>
        </div>

        {/* Type */}
        <div className="mx-6 w-24 flex items-center">
          <span className="text-sm text-[#444] truncate">
            {getFileType(document.name, document.content_type)}
          </span>
        </div>

        {/* Source */}
        <div className="mx-6 w-32 flex items-center">
          {document.document_kind !== 'Project' && onNavigateToTab ? (
            <button
              onClick={handleSourceClick}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#f5f5f5] text-[#555] hover:bg-[#eaeaea] transition-colors cursor-pointer"
              title={`Go to ${getSourceDisplay(document.document_kind)}`}
            >
              {getSourceDisplay(document.document_kind)}
            </button>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#f5f5f5] text-[#555]">
              {getSourceDisplay(document.document_kind)}
            </span>
          )}
        </div>

        {/* Owner */}
        <div className="mx-6 w-40 flex items-center">
          <div className="flex items-center space-x-2 min-w-0">
            <div className="w-6 h-6 bg-[#eaeaea] rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-medium text-[#555]">
                {document.owner.first_name.charAt(0)}{document.owner.last_name.charAt(0)}
              </span>
            </div>
            <span className="text-sm text-[#444] truncate">
              {document.owner.first_name} {document.owner.last_name}
            </span>
          </div>
        </div>

        {/* Size */}
        <div className="mx-6 w-20 flex items-center">
          <span className="text-sm text-[#888]">
            {formatFileSize(document.file_size)}
          </span>
        </div>

        {/* Actions */}
        <div className="ml-6 w-12 flex items-center justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering the row click
              onDelete(document);
            }}
            className="p-2 text-[#aaa] hover:text-[#dc2626] hover:bg-red-50 rounded-md transition-all duration-200"
            title="Delete"
          >
            <FaTrash className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}; 